/**
 * SOLVÉRA Judge.me presentation layer. Two features, both PRESENTATION-ONLY:
 * no review data is created, altered or deleted, and nothing here touches
 * Judge.me's own logic — if any selector stops matching after a Judge.me
 * update, each feature silently no-ops and the stock experience remains.
 *
 * 1) Review modal refinement (product pages): premium copy for labels,
 *    placeholders and the submit button, and a polite explanation for why
 *    email is asked (it IS technically required by Judge.me for verified-
 *    buyer matching — we keep the validation, soften the presentation).
 *
 * 2) Homepage review carousel: one review per unique customer. If a
 *    customer reviewed multiple products, their best review is chosen
 *    (photo > longest text > highest rating > most recent) and the rest are
 *    hidden — on the homepage only. Product pages keep every review.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * 1) Review modal refinement                                          *
   * ------------------------------------------------------------------ */
  var MODAL_SEL = '.jdgm-write-review-modal__container, .jdgm-review-widget-modal';
  var LABEL_SWAPS = [
    // [current Judge.me text, SOLVÉRA text] — exact-match only, so an
    // already-swapped label (or changed Judge.me copy) is left untouched.
    ['Review content', 'Tell us about your experience'],
    ['Email address (Required)', 'Email address'],
    ['Display name (Required)', 'Your name'],
    ['Display name', 'Your name'],
  ];
  var PLACEHOLDER_SWAPS = [
    ['Start writing here...', 'We would love to hear how it wears, feels and fits into your everyday.'],
    ['Display name', 'How your name should appear'],
  ];
  var SUBMIT_SWAPS = [['Leave a review', 'Share My Experience']];
  var EMAIL_NOTE = 'We use your email only to verify your review. It is never displayed publicly.';

  function refineModal(root) {
    var modal = (root || document).querySelector(MODAL_SEL);
    if (!modal) return;

    modal.querySelectorAll('.jdgm-write-review-modal__field-label, label').forEach(function (label) {
      var text = label.textContent.trim();
      LABEL_SWAPS.forEach(function (swap) {
        if (text === swap[0]) label.textContent = swap[1];
      });
    });

    modal.querySelectorAll('input, textarea').forEach(function (field) {
      PLACEHOLDER_SWAPS.forEach(function (swap) {
        if (field.placeholder === swap[0]) field.placeholder = swap[1];
      });
    });

    modal.querySelectorAll('button, input[type="submit"]').forEach(function (btn) {
      var text = (btn.value || btn.textContent || '').trim();
      SUBMIT_SWAPS.forEach(function (swap) {
        if (text === swap[0]) {
          if (btn.value) btn.value = swap[1];
          else btn.textContent = swap[1];
        }
      });
    });

    // Polite email explanation, inserted once under the email field.
    var email = modal.querySelector('input[type="email"]');
    if (email) {
      var group = email.closest('.jdgm-write-review-modal__field-group') || email.parentElement;
      if (group && !group.querySelector('.solvera-field-note')) {
        var note = document.createElement('p');
        note.className = 'solvera-field-note';
        note.textContent = EMAIL_NOTE;
        group.appendChild(note);
      }
    }
  }

  /* ------------------------------------------------------------------ *
   * 2) Homepage carousel: one review per unique customer                *
   * ------------------------------------------------------------------ */
  function isHomepage() {
    var p = location.pathname.replace(/\/+$/, '') || '/';
    return p === '/';
  }

  function cardScore(card) {
    // Priority: photo, then meaningful length, then rating, then recency.
    // Judge.me renders newest first, so a smaller DOM index means more
    // recent — encoded as a small tie-breaker.
    var hasPhoto = !!card.querySelector('img');
    var text = card.querySelector('.jdgm-text, [class*="text"], p');
    var len = Math.min(text ? text.textContent.trim().length : 0, 600);
    var rating = card.querySelectorAll('.jdgm-star.jdgm--on').length;
    return (hasPhoto ? 1e9 : 0) + len * 1000 + rating * 10;
  }

  function dedupeCarousel(wrapper) {
    var reals = wrapper.querySelectorAll('.jdgm-card:not(.jdgm-clone-before):not(.jdgm-clone-after)');
    if (!reals.length) return;

    var bestByAuthor = {}; // authorKey -> { id, score }
    reals.forEach(function (card, index) {
      var authorEl = card.querySelector('.jdgm-card__author, [class*="author"], [class*="reviewer"]');
      var id = card.getAttribute('data-review-id');
      if (!authorEl || !id) return; // structure changed — leave untouched
      var author = authorEl.textContent.trim().toLowerCase();
      if (!author) return;
      var score = cardScore(card) + (reals.length - index); // recency tie-break
      if (!bestByAuthor[author] || score > bestByAuthor[author].score) {
        bestByAuthor[author] = { id: id, score: score };
      }
    });

    var keep = {};
    Object.keys(bestByAuthor).forEach(function (a) { keep[bestByAuthor[a].id] = true; });
    if (!Object.keys(keep).length) return;

    var changed = false;
    // Hide every card (originals AND loop-clones) whose review lost, so the
    // same customer can never appear twice in a carousel cycle.
    wrapper.querySelectorAll('.jdgm-card[data-review-id]').forEach(function (card) {
      var shouldHide = !keep[card.getAttribute('data-review-id')];
      var isHidden = card.classList.contains('solvera-dup-hidden');
      if (shouldHide && !isHidden) {
        card.classList.add('solvera-dup-hidden');
        card.setAttribute('aria-hidden', 'true');
        changed = true;
      }
    });
    if (changed) window.dispatchEvent(new Event('resize')); // let the widget re-measure
  }

  function initCarousel() {
    if (!isHomepage()) return;
    var applied = false;
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      var wrapper = document.querySelector('.jdgm-cards-wrapper');
      if (wrapper && wrapper.querySelector('.jdgm-card[data-review-id]')) {
        dedupeCarousel(wrapper);
        applied = true;
        // keep it deduped if the widget re-renders/re-clones later
        new MutationObserver(function () { dedupeCarousel(wrapper); })
          .observe(wrapper, { childList: true, subtree: true });
        clearInterval(timer);
      } else if (tries > 60) { // ~30s: widget absent or blocked — no-op
        clearInterval(timer);
      }
    }, 500);
  }

  /* ------------------------------------------------------------------ *
   * 3) Review-first moments: popup truce + deep links                   *
   * ------------------------------------------------------------------ */
  // While the customer is writing a review, promotional popups (the Shopify
  // Forms discount embed) must never interrupt or steal clicks — verified
  // live: the embed opened ON TOP of the review modal. Presentation-only:
  // a class on <html> while the modal is visible; CSS hides the embed.
  function guardPopups() {
    var modal = document.querySelector('.jdgm-write-review-modal__container, .jdgm-review-widget-modal');
    var open = !!(modal && (modal.offsetWidth || modal.offsetHeight || modal.getClientRects().length));
    document.documentElement.classList.toggle('solvera-reviewing', open);
  }

  var REVIEWED_KEY = 'solvera:reviewed:v1';
  function currentHandle() {
    var m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]).toLowerCase() : null;
  }
  function markReviewed(handle) {
    if (!handle) return;
    try {
      var set = new Set(JSON.parse(localStorage.getItem(REVIEWED_KEY) || '[]'));
      if (!set.has(handle)) { set.add(handle); localStorage.setItem(REVIEWED_KEY, JSON.stringify([...set])); }
    } catch (e) { /* storage unavailable — hub simply won't show the badge */ }
  }

  // Premium review deep links: /review/<alias> is a Shopify URL redirect to
  // /products/<handle>?review=open — when that flag is present, open the
  // Judge.me write-review modal automatically so the customer lands straight
  // in the flow (no scrolling, no searching). Closing it leaves them on the
  // product page. From the Order Review Hub the link also carries
  // &hubback=<hub url>: on a detected submission we record the piece as
  // reviewed, and when the modal closes we return the customer to the hub.
  // No-ops silently if the widget is absent or renamed.
  function initDeepLink() {
    var params = new URLSearchParams(location.search);
    if (params.get('review') !== 'open') return;
    var hubback = params.get('hubback');
    var handle = currentHandle();
    var submitted = false;

    // Watch the modal for Judge.me's post-submit confirmation. We only ever
    // mark reviewed on a positive signal (safe failure mode: under-report,
    // never falsely claim a review). Judge.me shows a thank-you/success state.
    var successRe = /thank you|has been submitted|review submitted|successfully/i;
    var submitWatcher = new MutationObserver(function () {
      var modal = document.querySelector('.jdgm-write-review-modal__container');
      if (!modal || submitted) return;
      if (successRe.test(modal.textContent || '')) { submitted = true; markReviewed(handle); }
    });
    submitWatcher.observe(document.body, { childList: true, subtree: true, characterData: true });

    // When the modal closes, if this was a hub visit, return to the hub.
    var wasOpen = false;
    var closeWatcher = setInterval(function () {
      var modal = document.querySelector('.jdgm-write-review-modal__container');
      var open = !!(modal && (modal.offsetWidth || modal.offsetHeight || modal.getClientRects().length));
      if (open) wasOpen = true;
      if (wasOpen && !open && hubback) {
        clearInterval(closeWatcher);
        submitWatcher.disconnect();
        // small delay so a submit-then-autoclose still records first
        setTimeout(function () { location.assign(decodeURIComponent(hubback)); }, 150);
      }
    }, 400);

    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      var btn = document.querySelector('[data-testid="write-review-button"], .jdgm-write-rev-link');
      if (btn) {
        clearInterval(timer);
        btn.click();
        setTimeout(function () {
          refineModal();
          guardPopups();
          var modal = document.querySelector('.jdgm-write-review-modal__container');
          if (modal) modal.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 600);
      } else if (tries > 40) { // ~20s — widget missing; return to hub if applicable
        clearInterval(timer);
        if (hubback) location.assign(decodeURIComponent(hubback));
      }
    }, 500);
  }

  /* ------------------------------------------------------------------ */
  function init() {
    refineModal();
    guardPopups();
    // The modal is Vue-rendered and re-renders between its steps — keep the
    // refinements applied (all swaps are exact-match, hence idempotent).
    var mo = new MutationObserver(function () { refineModal(); guardPopups(); });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
    initCarousel();
    initDeepLink();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
